import { IPluginBinding, IPluginCard, IPluginField, IPluginParameterDto } from "@lib";
import { Modal } from "@core/components/alt-ui/modal";
import { Button, Control, Panel } from "@core/components/alt-ui/controls";
import { FormFieldControlFactory } from "@/core/types/form-field-controls/form-field-control-factory";
import { FormFieldControl } from "@/core/types/form-field-controls/form-field-control";

export type PluginParamsModalContext = {
    /** Плагин. */
    plugin: IPluginCard;

    /** Привязка плагина к компании. */
    binding: IPluginBinding;

    /** Обработчик сохранения параметров. */
    saveHandler: (binding: IPluginBinding, params: IPluginParameterDto[]) => Promise<IPluginBinding | null>;
};

export class PluginParamsModal extends Modal<PluginParamsModalContext, IPluginBinding> {
    private pnlMain!: Panel;
    private pnlFooter!: Panel;
    private btnSave!: Button;

    private context: PluginParamsModalContext | null = null;
    private fieldControls: FormFieldControl[] = [];

    public constructor() {
        super("plugin-params-modal", "Настройка интеграции");
        this.initializeControls();
    }

    public show(context: PluginParamsModalContext): Promise<IPluginBinding> {
        this.context = context;
        this.initializeControls();
        this.populateControls(context.binding);
        return super.show();
    }

    protected initializeControls(): void {
        this.fieldControls = [];
        this.pnlMain = new Panel();

        if (this.context?.plugin?.fields) {
            for (const field of this.context.plugin.fields) {
                const fieldControl = FormFieldControlFactory.fromField(field);
                this.fieldControls.push(fieldControl);

                fieldControl.control.class = "mb-0.75";
                this.pnlMain.addControl(fieldControl.control);
            }
        }

        this.btnSave = new Button();
        this.btnSave.id = "plugin-config.save";
        this.btnSave.text = "Сохранить";
        this.btnSave.addClickHandler(() => this.save());

        this.pnlFooter = new Panel();
        this.pnlFooter.class = "flex justify-end mt-2";
        this.pnlFooter.addControls([this.btnSave]);
    }

    private populateControls(binding: IPluginBinding): void {
        for (const fieldControl of this.fieldControls) {
            const field = fieldControl.field as IPluginField;

            if (!field?.alias) {
                continue;
            }

            fieldControl.value = binding.params?.find(p => p.name === field.alias)?.value ?? "";
        }
    }

    public get controls(): Control[] {
        return [this.pnlMain, this.pnlFooter];
    }

    private async save(): Promise<void> {
        const valid = await this.validate();

        if (!valid || !this.context?.saveHandler) {
            return;
        }

        const dto: IPluginParameterDto[] = [];

        for (const fieldControl of this.fieldControls) {
            const field = fieldControl.field as IPluginField;

            if (!field?.alias) {
                continue;
            }

            dto.push({
                name: field.alias,
                value: fieldControl.value,
                secret: field.secret ?? field.details?.secret,
            });

            if (field.params && fieldControl.hasValue) {
                for (const param of field.params) {
                    dto.push({
                        name: param.name,
                        value: param.value,
                    });
                }
            }
        }

        const result = await this.context.saveHandler(this.context.binding, dto);

        if (result) {
            this.hide(result);
        }
    }
}
